import {
  $createTableNodeWithDimensions,
  $createTableRowNode,
  $createTableCellNode,
  $deleteTableColumnAtSelection,
  $isTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowNodeFromTableCellNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $getTableColumnIndexFromTableCellNode,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $findTableNode,
} from '@lexical/table';
import { $createParagraphNode } from 'lexical';

import type { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import type { TextNode, ElementNode, LexicalEditor } from 'lexical';

import { TableWrapperNode } from 'components/RteEditor/nodes';

export const MIN_COLUMN_WIDTH = 32;
export const DEFAULT_COL_WIDTH = 48;
export const BORDER_WIDTHS = 1;
export const PREVENT_OPEN_DELAY = 200;
export const DEFAULT_EDITOR_WIDTH = 320;

export const createTable = (nrOfRows: number, nrOfColumns: number): ElementNode => {
  const tableNode = $createTableNodeWithDimensions(0, 0, false);
  for (let rowIndex = 0; rowIndex < nrOfRows; rowIndex++) {
    const rowNode = $createTableRowNode();
    for (let colIndex = 0; colIndex < nrOfColumns; colIndex++) {
      const cellNode = $createTableCellNode(0, undefined, undefined);
      cellNode.append($createParagraphNode());
      rowNode.append(cellNode);
    }
    tableNode.append(rowNode);
  }
  return tableNode;
};

export const deleteRow = (selectedNode: ElementNode) => {
  const currentRow = $getTableRowNodeFromTableCellNodeOrThrow(selectedNode);
  const nextRow = currentRow.getNextSibling() || currentRow.getPreviousSibling();
  const currentTable = currentRow.getParentOrThrow();
  currentTable.getChildrenSize() > 1 ? currentRow.remove() : currentTable.remove();

  if (nextRow !== null) {
    const firstDescendant = (nextRow as ElementNode).getFirstDescendant();
    if (firstDescendant) {
      (firstDescendant as TextNode).select();
    }
  }
};

export const deleteTable = (selectedNode: ElementNode) => {
  const tableNode = $findTableNode(selectedNode);
  if (!tableNode) {
    throw new Error('Selected node is not a TableCellNode');
  }

  const wrapperNode = tableNode.getParent();
  if (wrapperNode instanceof TableWrapperNode) {
    wrapperNode.remove();
  } else {
    tableNode.remove();
  }
};

export const insertRow = (selectedNode: ElementNode, position: 'insertRowAbove' | 'insertRowBelow') => {
  if (!$isTableCellNode(selectedNode)) {
    throw new Error('Selected node is not a TableCellNode');
  }
  const newRow = $createTableRowNode();

  $insertTableRowAtSelection(position === 'insertRowBelow');

  const firstDescendant = newRow.getFirstDescendant();
  if (firstDescendant) {
    (firstDescendant as TextNode).select();
  }
};

export const insertColumn = (selectedNode: ElementNode, position: 'insertColumnLeft' | 'insertColumnRight') => {
  if (!$isTableCellNode(selectedNode)) {
    throw new Error('Selected node is not a TableCellNode');
  }
  const currentTable = $getTableNodeFromLexicalNodeOrThrow(selectedNode);
  let columnIndex = $getTableColumnIndexFromTableCellNode(selectedNode);
  const rowsInTable: TableCellNode[] = currentTable.getChildren();
  const rowIndex = $getTableRowIndexFromTableCellNode(selectedNode);
  const insertedCellNode = $insertTableColumnAtSelection(position === 'insertColumnLeft');

  if (!insertedCellNode) return;
  const insertedColumnIndex = $getTableColumnIndexFromTableCellNode(insertedCellNode);

  if (insertedColumnIndex === -1) return;
  const colWidths = currentTable.getColWidths();

  if (!colWidths) return;
  const newColWidths = colWidths.map((width, index) =>
    index === insertedColumnIndex ? DEFAULT_COL_WIDTH : width - Math.ceil(DEFAULT_COL_WIDTH / (colWidths.length - 1))
  );

  currentTable.setColWidths(newColWidths);

  columnIndex = position === 'insertColumnLeft' ? columnIndex : columnIndex + 1;
  const firstDescendant = rowsInTable[rowIndex].getChildren()[columnIndex];
  if (firstDescendant) {
    (firstDescendant as TextNode).select();
  }
};

export const deleteColumn = (selectedNode: ElementNode) => {
  if (!$isTableCellNode(selectedNode)) {
    throw new Error('Selected node is not a TableCellNode');
  }
  const currentTable = $getTableNodeFromLexicalNodeOrThrow(selectedNode);
  const numberOfColumns = (currentTable.getFirstChild() as ElementNode)?.getChildrenSize() || 0;

  if (numberOfColumns === 1) {
    deleteTable(selectedNode);
  } else {
    $deleteTableColumnAtSelection();
  }
};

export const getEditorContentWidth = (editor: LexicalEditor): number => {
  const rootEl = editor.getRootElement();
  if (!rootEl || typeof window === 'undefined') return 0;
  const rect = rootEl.getBoundingClientRect();
  const styles = window.getComputedStyle(rootEl);
  const insertX = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0') + DEFAULT_COL_WIDTH;
  return Math.max(0, rect.width - insertX);
};

export const getEditorParrentCellWidth = (
  node: TableCellNode | null,
  nrOfColumns: number,
  editor: LexicalEditor
): number => {
  if (!node) return 0;

  const cell = editor.getElementByKey(node.getKey());
  if (!cell) return 0;

  const computedStyles = window.getComputedStyle(cell);
  return parseInt(computedStyles.width) - nrOfColumns * BORDER_WIDTHS - 1;
};

export const tableHasContent = (tableNode: TableNode): boolean => {
  const rows = tableNode.getChildren();
  for (const row of rows) {
    const cells = (row as TableRowNode).getChildren();
    for (const cell of cells) {
      if ($isTableCellNode(cell)) {
        const cellText = cell.getTextContent().trim();
        if (cellText.length > 0) {
          return true;
        }
      }
    }
  }
  return false;
};
